import {
  Container,
  Box,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Button,
  Divider
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ContractListFilter,
  ContractListLabel,
  ContractTableLabel,
  ContractListDatas
} from '../../types'
import {
  isEmpty,
  isNil,
  keys,
  map,
  filter,
  includes,
  reduce,
  isArray
} from 'lodash/fp'
import {
  format,
  isAfter,
  isBefore,
  parse,
  parseISO,
  subDays,
  isWithinInterval,
  addDays
} from 'date-fns/fp'
import { Controller, useForm } from 'react-hook-form'
import log from 'loglevel'
import Encoding from 'encoding-japanese'

import { getCustomerList, trimSpace } from '../../functions/common'
import filterStatusConfig from '../../assets/filterStatusConfig.json'
import incompleteConfig from '../../assets/incompleteConfig.json'
import { inspect } from '../Progress/inspect'
import { parseCsv } from './functions'
import { Loading } from '../Loading'
import { Link } from 'react-router-dom'

const formatListDate = (date?: string) =>
  !isNil(date) && !isEmpty(date) ? format('yyyy-MM-dd')(parseISO(date)) : ''

const isBetweenDate = (
  date: string | undefined,
  from: string | undefined,
  to: string | undefined
): boolean => {
  if (isEmpty(date)) {
    return false
  }
  const parsedFrom = !isEmpty(from) ? parseISO(from) : ''
  const parsedTo = !isEmpty(to) ? parseISO(to) : ''
  const parsedContractDate = parse(Date.now(), 'yyyy-MM-dd')(date)
  if (isEmpty(from) && isEmpty(to)) {
    return false
  } else if (isEmpty(from)) {
    return !isBefore(parsedFrom)(parsedContractDate)
  } else if (isEmpty(to)) {
    return !isAfter(parsedTo)(parsedContractDate)
  } else {
    return isWithinInterval({
      start: subDays(1)(parsedFrom),
      end: addDays(1)(parsedTo)
    })(parsedContractDate)
  }
}

const ContractListHeader = (props: {
  handleSort: Function
  sortConfig: {
    key: string
    direction: string
  }
}) => {
  const { handleSort, sortConfig } = props
  return (
    <TableHead sx={{ backgroundColor: 'orange' }}>
      <TableRow>
        {map((key: string) => {
          return (
            <TableCell
              onClick={() => handleSort(key)}
              style={{ cursor: 'pointer' }}>
              {ContractTableLabel[key as keyof typeof ContractTableLabel]}
              {sortConfig.key === key
                ? sortConfig.direction === 'asc'
                  ? '▲'
                  : '▼'
                : ''}
            </TableCell>
          )
        })(keys(ContractTableLabel))}
      </TableRow>
    </TableHead>
  )
}
const ContractListBody = (props: { paginatedRows: ContractListDatas[] }) => {
  const { paginatedRows } = props
  return (
    <TableBody>
      {map((row: ContractListDatas) => (
        <TableRow key={row.memberId}>
          <TableCell>
            <Link to={`/admin/update/${row.memberId}`}>{row.name}</Link>
          </TableCell>
          <TableCell>{row.nameKana}</TableCell>
          <TableCell>
            {
              filterStatusConfig[
              row.statusCd as keyof typeof filterStatusConfig
              ]
            }
          </TableCell>
          <TableCell>
            {!isEmpty(row.incompleteCd)
              ? `${row.incompleteCd}: ${incompleteConfig[
              row.incompleteCd as keyof typeof incompleteConfig
              ]
              }`
              : ''}
          </TableCell>
          <TableCell>{formatListDate(row.applicationDate)}</TableCell>
          <TableCell>{formatListDate(row.contractDate)}</TableCell>
          <TableCell>{formatListDate(row.scheduledAbolitionDate)}</TableCell>
          <TableCell>{formatListDate(row.cancellationDate)}</TableCell>
          <TableCell>{row.couponCode}</TableCell>
          <TableCell>{row.email}</TableCell>
        </TableRow>
      ))(paginatedRows)}
    </TableBody>
  )
}

export const ContractList = () => {
  const [initialRows, setInitialRows] = useState<ContractListDatas[]>([])
  const rowsPerPage = 10
  const [rows, setRows] = useState<ContractListDatas[]>(initialRows)
  const [page, setPage] = useState<number>(1)
  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: string
  }>({ key: '', direction: '' })
  const [loading, setLoading] = useState<boolean>(true)
  const downloadRef = useRef<HTMLAnchorElement>(null)
  const formMethods = useForm<ContractListFilter>({})
  const {
    register,
    handleSubmit,
    reset,
    control
  } = formMethods

  useEffect(() => {
    const loadList = async () => {
      const list = await getCustomerList()
      log.debug('[ContractList]', inspect({ list }))
      setInitialRows(list)
      setRows(list)
      setSortConfig({ key: 'applicationDate', direction: 'desc' })
      setLoading(false)
    }
    loadList()
  }, [])

  const handleSort = (key: string) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === 'asc'
    const direction = isAsc ? 'desc' : 'asc'
    setSortConfig({ key, direction })
    setRows(sortedRows)
  }
  const handleClickSearch = (data: ContractListFilter) => {
    const {
      contractDateFrom,
      contractDateTo,
      applicationDateFrom,
      applicationDateTo,
      cancellationDateFrom,
      cancellationDateTo,
      scheduledAbolitionDateFrom,
      scheduledAbolitionDateTo
    } = data
    const filterd = filter((row: ContractListDatas) => {
      return reduce((result: boolean, key: string) => {
        if (isEmpty(data[key as keyof typeof data])) return result
        if (includes(key)(['contractDateFrom', 'contractDateTo'])) {
          result = isBetweenDate(
            row.contractDate,
            contractDateFrom,
            contractDateTo
          )
            ? result
            : false
        } else if (
          includes(key)(['applicationDateFrom', 'applicationDateTo'])
        ) {
          result = isBetweenDate(
            row.applicationDate,
            applicationDateFrom,
            applicationDateTo
          )
            ? result
            : false
        } else if (
          includes(key)(['cancellationDateFrom', 'cancellationDateTo'])
        ) {
          result = isBetweenDate(
            row.cancellationDate,
            cancellationDateFrom,
            cancellationDateTo
          )
            ? result
            : false
        } else if (
          includes(key)([
            'scheduledAbolitionDateFrom',
            'scheduledAbolitionDateTo'
          ])
        ) {
          result = isBetweenDate(
            row.scheduledAbolitionDate,
            scheduledAbolitionDateFrom,
            scheduledAbolitionDateTo
          )
            ? result
            : false
        } else if (isArray(data[key as keyof typeof data])) {
          result = includes(trimSpace(row[key as keyof typeof row] as string))(
            data[key as keyof typeof data]
          )
            ? result
            : false
        } else {
          result =
            !isEmpty(row[key as keyof typeof row]) &&
              includes(trimSpace(data[key as keyof typeof data] as string))(
                trimSpace(row[key as keyof typeof row] as string)
              )
              ? result
              : false
        }
        return result
      }, true)(keys(data))
    })(initialRows)
    setRows(filterd)
    setSortConfig({ key: sortConfig.key, direction: sortConfig.direction })
    setPage(1)
  }
  const handleClickClear = () => {
    setSortConfig({ key: 'applicationDate', direction: 'desc' })
    setRows(initialRows)
    setPage(1)
    reset()
  }
  const handleClickDownload = () => {
    const link = downloadRef.current
    if (isNil(link)) return
    const sjis = Encoding.convert(parseCsv(ContractListLabel, rows), {
      to: 'SJIS',
      from: 'UNICODE',
      type: 'arraybuffer'
    })
    const unit8Array = new Uint8Array(sjis)
    const blob = new Blob([unit8Array], {
      type: 'text/csv;charset=shift-jis;'
    })
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', format('yyyy-MM-dd')(Date.now()))
    link.click()
  }
  const sortedRows = useMemo(() => {
    const { key, direction } = sortConfig
    return [...rows].sort(
      (item: ContractListDatas, subItem: ContractListDatas) => {
        if (
          isEmpty(item[key as keyof typeof item]) &&
          isEmpty(subItem[key as keyof typeof item])
        )
          return 0
        if (isEmpty(item[key as keyof typeof item]))
          return direction === 'asc' ? -1 : 1
        if (isEmpty(subItem[key as keyof typeof item]))
          return direction === 'asc' ? 1 : -1
        if (
          (item[key as keyof typeof item] as string) <
          (subItem[key as keyof typeof subItem] as string)
        )
          return direction === 'asc' ? -1 : 1
        if (
          (item[key as keyof typeof item] as string) >
          (subItem[key as keyof typeof subItem] as string)
        )
          return direction === 'asc' ? 1 : -1
        return 0
      }
    )
  }, [sortConfig])
  const paginatedRows = useMemo(() => {
    return sortedRows.slice((page - 1) * rowsPerPage, page * rowsPerPage)
  }, [page, sortedRows])
  return loading ? (
    <Loading />
  ) : (
    <>
      <Container sx={{ pt: 5 }}>
        <Box component="form" sx={{ mt: 1 }}>
          <Stack spacing={3} direction="row" useFlexGap>
            <TextField
              id="mail"
              label="TCIDメールアドレス"
              {...register('email')}
              placeholder=""
              required
              variant="filled"
              margin="dense"></TextField>
            <TextField
              id="couponCode"
              label="紹介コード"
              placeholder=""
              required
              variant="filled"
              {...register('couponCode')}
              margin="dense"></TextField>
            <FormControl>
              <InputLabel id="contractStatusLabel">契約ステータス</InputLabel>
              <Controller
                name="statusCd"
                defaultValue={[]}
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      {...field}
                      labelId="contractStatusLabel"
                      id="statusCd"
                      variant="filled"
                      sx={{ minWidth: 200, mt: 1 }}
                      multiple>
                      {map((key: string) => (
                        <MenuItem value={key}>
                          {
                            filterStatusConfig[
                            key as keyof typeof filterStatusConfig
                            ]
                          }
                        </MenuItem>
                      ))(keys(filterStatusConfig))}
                    </Select>
                  )
                }}
              />
            </FormControl>
          </Stack>
          <Stack spacing={3} direction="row" useFlexGap>
            <TextField
              id="name"
              label="契約者"
              placeholder=""
              required
              variant="filled"
              {...register('name')}
              margin="dense"></TextField>
            <TextField
              id="nameKana"
              label="契約者（かな）"
              placeholder=""
              required
              variant="filled"
              {...register('nameKana')}
              margin="dense"></TextField>
            <FormControl>
              <InputLabel id="incompleteCdLabel">申込不備内容</InputLabel>
              <Controller
                name="incompleteCd"
                defaultValue={[]}
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      {...field}
                      labelId="incompleteCdLabel"
                      id="incompleteCd"
                      fullWidth
                      variant="filled"
                      sx={{ minWidth: 200, mt: 1 }}
                      multiple>
                      {map((key: string) => (
                        <MenuItem value={key}>
                          {
                            incompleteConfig[
                            key as keyof typeof incompleteConfig
                            ]
                          }
                        </MenuItem>
                      ))(keys(incompleteConfig))}
                    </Select>
                  )
                }}
              />
            </FormControl>
          </Stack>
          <Stack spacing={3} direction="row" alignItems={'center'} useFlexGap>
            <TextField
              id="applicationDateFrom"
              label="申込日(開始)"
              type="date"
              required
              variant="filled"
              {...register('applicationDateFrom')}
              margin="dense"></TextField>
            <Typography>~</Typography>
            <TextField
              id="applicationDateTo"
              label="申込日(終了)"
              type="date"
              required
              variant="filled"
              {...register('applicationDateTo')}
              margin="dense"></TextField>
            <TextField
              id="cancellationDateFrom"
              label="解約予定日(開始)"
              type="date"
              required
              variant="filled"
              {...register('cancellationDateFrom')}
              margin="dense"></TextField>
            <Typography>~</Typography>
            <TextField
              id="cancellationDateTo"
              label="解約予定日(終了)"
              type="date"
              required
              variant="filled"
              {...register('cancellationDateTo')}
              margin="dense"></TextField>
          </Stack>
          <Stack spacing={3} direction="row" alignItems={'center'} useFlexGap>
            <TextField
              id="contractDateFrom"
              label="契約日(開始)"
              type="date"
              required
              variant="filled"
              {...register('contractDateFrom')}
              margin="dense"></TextField>
            <Typography>~</Typography>
            <TextField
              id="contractDateTo"
              label="契約日(終了)"
              type="date"
              required
              variant="filled"
              {...register('contractDateTo')}
              margin="dense"></TextField>
            <TextField
              id="scheduledAbolitionDateFrom"
              label="廃止予定日(開始)"
              type="date"
              required
              variant="filled"
              {...register('scheduledAbolitionDateFrom')}
              margin="dense"></TextField>
            <Typography>~</Typography>
            <TextField
              id="scheduledAbolitionDateTo"
              label="廃止予定日(終了)"
              type="date"
              required
              variant="filled"
              {...register('scheduledAbolitionDateTo')}
              margin="dense"></TextField>
          </Stack>
          <Stack
            direction={'row'}
            display={'flex'}
            justifyContent={'space-around'}
            paddingTop={3}>
            <Button onClick={handleSubmit(handleClickSearch)}>検索</Button>
            <Button onClick={handleClickClear}>クリア</Button>
          </Stack>
          <Divider />
          <Box sx={{ margin: '20px auto' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                margin: '20px 0'
              }}>
              <Stack direction={'row'}>
                <Pagination
                  count={Math.ceil(rows.length / rowsPerPage)}
                  page={page}
                  onChange={(_event, value) => setPage(value)}
                  color="primary"
                />
                <Button onClick={handleClickDownload}>CSVダウンロード</Button>
                {/* eslint-disable jsx-a11y/anchor-is-valid */}
                <a ref={downloadRef} className="hidden"> </a>
              </Stack>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <ContractListHeader
                  handleSort={handleSort}
                  sortConfig={sortConfig}
                />
                <ContractListBody paginatedRows={paginatedRows} />
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Container>
    </>
  )
}
